import React, { useRef, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";

const SignaturePad = ({ isOpen, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 560;
    canvas.height = 190;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#000000";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  useEffect(() => {
    if (isOpen) {
      initializeCanvas();
    }
  }, [isOpen]);

  const getOffset = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    };
  };

  const startDrawing = (e) => {
    const { x, y } = getOffset(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getOffset(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSigned(true);
  };

  const stopDrawing = () => {
    setDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const saveSignature = () => {
    if (!hasSigned) {
      return;
    }

    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onEntered: initializeCanvas,
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "GilroyBold",
          fontSize: "18px",
          pb: 0.5,
        }}
      >
        Add Signature
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          sx={{
            fontFamily: "gilroyMedium",
            fontSize: "14px",
            color: "#555",
            mb: 1.5,
          }}
        >
          Sign inside the box below.
        </Typography>

        <Box
          sx={{
            border: "1px solid #d0d7de",
            borderRadius: "8px",
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{
              width: "100%",
              maxWidth: "100%",
              height: "190px",
              display: "block",
              cursor: "crosshair",
              touchAction: "none",
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={clearCanvas}
          sx={{ fontFamily: "gilroyBold", textTransform: "none" ,fontSize:"14px" }}
        >
          Clear
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ fontFamily: "gilroyBold", textTransform: "none",fontSize:"14px" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveSignature}
          disabled={!hasSigned}
          sx={{ fontFamily: "gilroyBold", textTransform: "none",fontSize:"14px" }}
        >
          Save Signature
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignaturePad;
